import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight, Loader2, User, Building2, Calendar, Star, ClipboardList,
  ClipboardCheck, AlertOctagon, ShieldCheck, Send, TrendingUp, School,
} from "lucide-react";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("ar-SA"); } catch { return "—"; } };

const STATUS_META = {
  open:        { label: "مفتوحة",     cls: "bg-rose-50 text-rose-700 border-rose-200" },
  in_progress: { label: "قيد المعالجة", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  resolved:    { label: "تم حلّها",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  dismissed:   { label: "مُصنَّفة كغير جوهرية", cls: "bg-slate-50 text-slate-600 border-slate-200" },
};

const PRIORITY_META = {
  low:    { label: "عادية",   cls: "bg-slate-50 text-slate-700 border-slate-200" },
  medium: { label: "متوسطة", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  high:   { label: "عالية",   cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

export default function MentorStudentDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/mentor/students/${id}/detail`);
      setData(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const activePlacement = useMemo(() => (
    (data?.placements || []).find((p) => ["accepted", "assigned"].includes(p.status)) || (data?.placements || [])[0]
  ), [data]);

  if (loading) return <DashboardLayout><div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div></DashboardLayout>;
  if (error) return <DashboardLayout><div className="p-4 rounded-2xl border border-red-200 bg-red-50 text-red-700">{error}</div></DashboardLayout>;
  if (!data) return null;

  const { student, tasks, complaints } = data;

  return (
    <DashboardLayout>
      <div data-testid="mentor-student-detail">
        <Link to="/dashboard/my-students" className="inline-flex items-center gap-1.5 text-sm text-teal-700 font-semibold hover:text-teal-800 mb-6" data-testid="back-to-my-students">
          <ArrowRight className="h-4 w-4" /> رجوع للوحة طلابي
        </Link>

        <section className="rounded-2xl bg-gradient-to-l from-teal-700 to-teal-800 text-white p-4 mb-8 card-shadow" data-testid="identity-card">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 rounded-full bg-white/10 grid place-items-center text-white font-heading font-black text-2xl">
              {student.name?.[0] || <User className="h-6 w-6" />}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-heading font-black text-2xl">{student.name}</h1>
              <div className="mt-1 text-teal-100 text-sm flex flex-wrap gap-x-3 gap-y-1">
                {student.university && <span className="inline-flex items-center gap-1"><School className="h-3.5 w-3.5" />{student.university}</span>}
                {student.college && <span>· {student.college}</span>}
                {student.major && <span>· {student.major}</span>}
              </div>
              <div className="mt-1 text-teal-100 text-xs">{student.email}</div>
              {student.phone && <div className="mt-1 text-teal-100 text-xs">{student.phone}</div>}
            </div>
          </div>
        </section>

        {activePlacement ? (
          <section className="grid lg:grid-cols-3 gap-5 mb-8">
            <PlacementCard placement={activePlacement} />
            <AttendanceCard attendance={activePlacement.attendance} />
            <EvalsCard placement={activePlacement} />
          </section>
        ) : (
          <div className="mb-8 p-4 rounded-2xl border border-dashed border-slate-300 bg-white text-sm text-slate-600">
            لا يوجد تدريب نشط لهذا الطالب حالياً.
          </div>
        )}

        {/* Tasks */}
        <section className="mb-8">
          <SectionHeader icon={ClipboardList} title="مهام الطالب مع الجمعية" count={tasks.length} testid="tasks-section" />
          {tasks.length === 0 ? (
            <EmptyStrip text="لم تُكلَّف أي مهام بعد." />
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="tasks-grid">
              {tasks.map((t) => (
                <div key={t.id} className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid={`task-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-bold text-slate-900">{t.title}</div>
                      {t.association_name && <div className="text-xs text-slate-500 mt-0.5">{t.association_name}</div>}
                    </div>
                    <TaskStatusChip status={t.status} />
                  </div>
                  {t.description && <p className="mt-2 text-sm text-slate-700 line-clamp-3">{t.description}</p>}
                  <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                    {t.due_date && <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{fmtDate(t.due_date)}</span>}
                    {t.score != null && (
                      <span className="inline-flex items-center gap-1 font-bold text-amber-700 arabic-numerals">
                        <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" /> {t.score}/100
                      </span>
                    )}
                  </div>
                  {t.association_feedback && (
                    <div className="mt-3 p-2.5 rounded-lg bg-emerald-50 border border-emerald-100 text-xs">
                      <div className="font-bold text-emerald-700 mb-0.5">ملاحظات الجمعية</div>
                      <div className="text-slate-800 whitespace-pre-line">{t.association_feedback}</div>
                    </div>
                  )}
                  <Link to={"/dashboard?task=" + t.id} className="mt-3 inline-flex min-h-11 items-center rounded-xl border border-teal-200 px-3 text-xs font-bold text-teal-800" data-testid={"open-task-" + t.id}>فتح تفاصيل المهمة وتقييمها</Link>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Complaints */}
        <section id="complaints" className="mb-4 scroll-mt-6">
          <div className="mb-3 flex items-center gap-2">
            <AlertOctagon className="h-5 w-5 text-teal-700" />
            <h2 className="font-heading font-black text-slate-900">معالجة الشكاوى</h2>
            <span className="ms-auto text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold arabic-numerals">{complaints.length}</span>
          </div>

          {complaints.length === 0 ? (
            <EmptyStrip text="لا توجد شكاوى مسجّلة على هذا الطالب." />
          ) : (
            <div className="space-y-3" data-testid="complaints-list">
              {complaints.map((c) => <ComplaintRow key={c.id} complaint={c} onChange={load} />)}
            </div>
          )}
        </section>

      </div>
    </DashboardLayout>
  );
}

function SectionHeader({ icon: Icon, title, count, testid }) {
  return (
    <div className="mb-3 flex items-center gap-2" data-testid={`${testid}-header`}>
      <Icon className="h-5 w-5 text-teal-700" />
      <h2 className="font-heading font-black text-slate-900">{title}</h2>
      <span className="ms-auto text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold arabic-numerals">{count}</span>
    </div>
  );
}

function EmptyStrip({ text }) {
  return <div className="p-4 rounded-xl border border-dashed border-slate-300 bg-white text-sm text-slate-500">{text}</div>;
}

function TaskStatusChip({ status }) {
  const map = {
    done:      { label: "مكتملة", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    submitted: { label: "بانتظار التقييم", cls: "bg-sky-50 text-sky-700 border-sky-200" },
    open:      { label: "مفتوحة", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  };
  const m = map[status] || { label: status || "—", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${m.cls}`}>{m.label}</span>;
}

function PlacementCard({ placement }) {
  return (
    <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="placement-card">
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="h-5 w-5 text-teal-700" />
        <div className="text-xs font-bold uppercase tracking-widest text-teal-700">التدريب مع الجمعية</div>
      </div>
      <div className="font-heading font-black text-slate-900">{placement.opportunity_title || "—"}</div>
      <div className="mt-3 space-y-2 text-sm">
        <Row label="الجمعية" value={placement.association_name} />
        <Row label="بريد الجمعية" value={placement.association_email} />
        <Row label="هاتف الجمعية" value={placement.association_phone} />
        <Row label="مدينة الجمعية" value={placement.association_city} />
        <Row label="الفترة" value={placement.training_start ? `${fmtDate(placement.training_start)} — ${fmtDate(placement.training_end)}` : "—"} />
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-baseline gap-2 border-b border-slate-100 last:border-0 pb-1.5 last:pb-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="font-semibold text-slate-800 text-sm text-end">{value || "—"}</span>
    </div>
  );
}

function AttendanceCard({ attendance }) {
  const a = attendance || {};
  const present = Number(a.present_days || 0);
  const absent = Number(a.absent_days || 0);
  const total = present + absent;
  const pct = total > 0 ? Math.round((present / total) * 100) : 0;
  return (
    <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="attendance-card">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="h-5 w-5 text-teal-700" />
        <div className="text-xs font-bold uppercase tracking-widest text-teal-700">الحضور والغياب</div>
      </div>
      {total === 0 ? (
        <div className="text-sm text-slate-500 py-2">لم تُسجَّل بيانات حضور بعد.</div>
      ) : (
        <>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-heading font-black text-emerald-700 arabic-numerals">{pct}%</span>
            <span className="text-xs text-slate-500">نسبة الحضور</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-1">
            {Array.from({ length: present }).map((_, i) => (
              <span key={`p-${i}`} className="h-4 w-2 rounded-sm bg-emerald-500" title="حضور" />
            ))}
            {Array.from({ length: absent }).map((_, i) => (
              <span key={`a-${i}`} className="h-4 w-2 rounded-sm bg-rose-400" title="غياب" />
            ))}
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
            <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-100"><div className="font-bold text-emerald-700 arabic-numerals">{present}</div><div className="text-slate-500">حضور</div></div>
            <div className="p-2 rounded-lg bg-rose-50 border border-rose-100"><div className="font-bold text-rose-700 arabic-numerals">{absent}</div><div className="text-slate-500">غياب</div></div>
            <div className="p-2 rounded-lg bg-teal-50 border border-teal-100"><div className="font-bold text-teal-700 arabic-numerals">{a.total_hours || 0}</div><div className="text-slate-500">ساعة</div></div>
          </div>
        </>
      )}
    </div>
  );
}

function EvalsCard({ placement }) {
  return (
    <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="evals-card">
      <div className="flex items-center gap-2 mb-3">
        <ClipboardCheck className="h-5 w-5 text-teal-700" />
        <div className="text-xs font-bold uppercase tracking-widest text-teal-700">التقييمات</div>
      </div>
      <EvalBlock title="تقييم الجمعية" score={placement.association_score} notes={placement.association_notes} tint="from-emerald-50 to-emerald-100 text-emerald-800 border-emerald-200" />
      <div className="mt-3">
        <EvalBlock title="تقييمي كموجّه" score={placement.mentor_score} notes={placement.mentor_notes} tint="from-indigo-50 to-indigo-100 text-indigo-800 border-indigo-200" />
      </div>
    </div>
  );
}

function EvalBlock({ title, score, notes, tint }) {
  return (
    <div className={`rounded-xl border bg-gradient-to-l ${tint} p-3`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold">{title}</span>
        {score != null ? (
          <span className="inline-flex items-center gap-1 font-heading font-black arabic-numerals">
            <Star className="h-4 w-4 fill-amber-500 text-amber-500" /> {score}/100
          </span>
        ) : <span className="text-xs opacity-70">لم يُقيَّم بعد</span>}
      </div>
      {notes && <div className="mt-1 text-xs whitespace-pre-line opacity-90">{notes}</div>}
    </div>
  );
}

function ComplaintRow({ complaint, onChange }) {
  const s = STATUS_META[complaint.status] || STATUS_META.open;
  const p = PRIORITY_META[complaint.priority] || PRIORITY_META.medium;
  const [note, setNote] = useState(complaint.resolution_note || "");
  const [busy, setBusy] = useState(false);

  const patch = async (fields) => {
    setBusy(true);
    try {
      await api.patch(`/complaints/${complaint.id}`, fields);
      toast.success("تم تحديث الشكوى");
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const closed = ["resolved", "dismissed"].includes(complaint.status);

  return (
    <div id={`complaint-`} className="scroll-mt-6 p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid={`complaint-`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-heading font-bold text-slate-900">{complaint.subject}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            بواسطة <span className="font-bold text-teal-700">{complaint.filed_by_name}</span> ({complaint.filed_by_role}) · {fmtDate(complaint.created_at)}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${p.cls}`}>{p.label}</span>
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${s.cls}`}>{s.label}</span>
        </div>
      </div>
      <p className="mt-3 text-sm text-slate-700 whitespace-pre-line">{complaint.body}</p>

      {complaint.resolution_note && (
        <div className="mt-3 p-2.5 rounded-lg bg-emerald-50 border border-emerald-100 text-sm">
          <div className="text-[11px] font-bold text-emerald-700 mb-0.5">ملاحظة الإغلاق</div>
          <div className="text-slate-800 whitespace-pre-line">{complaint.resolution_note}</div>
        </div>
      )}

      {!closed && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <textarea
            value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            placeholder="اكتب طريقة معالجة الشكوى قبل إغلاقها (اختياري)"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            data-testid={`complaint-note-${complaint.id}`}
          />

          <div className="mt-2 flex flex-wrap gap-2 justify-end">
            {complaint.status === "open" && (
              <button disabled={busy} onClick={() => patch({ status: "in_progress" })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-bold hover:bg-amber-600 disabled:opacity-60" data-testid={`inprogress-${complaint.id}`}>
                بدء المعالجة
              </button>
            )}
            <button disabled={busy} onClick={() => patch({ status: "resolved", resolution_note: note })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-60" data-testid={`resolve-${complaint.id}`}>
              <ShieldCheck className="h-3 w-3" /> تم الحل
            </button>
            <button disabled={busy} onClick={() => patch({ status: "dismissed", resolution_note: note })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-500 text-white text-xs font-bold hover:bg-slate-600 disabled:opacity-60" data-testid={`dismiss-${complaint.id}`}>
              رفض
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
